
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, ChevronDown, Building2,
  BadgeCheck, Shield, LogOut, LayoutDashboard
} from "lucide-react";
import Logo from "../assets/logo2.jpg";

const API = (process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com").replace(/\/$/, "");
const SESSION_KEY = "infra_v5_supremeadmin";
const LOGIN_PATH = "/supremeadmin/login";

function safeParse(value) {
  try { return JSON.parse(value); } catch { return null; }
}

function DropdownMenu({ title, icon: Icon, index, openIndex, setOpenIndex, children }) {
  const isOpen = openIndex === index;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-slate-100 transition hover:bg-slate-800/70"
      >
        <span className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span className="font-medium">{title}</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <ul className="ml-3 mt-1 space-y-1 border-l border-slate-700 pl-3">
          {children}
        </ul>
      )}
    </div>
  );
}

export default function SuperAdminNav() {
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [user, setUser] = useState(() =>
    safeParse(sessionStorage.getItem(SESSION_KEY))
  );
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    setUser(safeParse(sessionStorage.getItem(SESSION_KEY)));
  }, [location.pathname]);

  // Authentication redirects are handled by the App.js route guard.
  // Do not redirect from the navigation component.

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await axios.post(`${API}/api/supremeadmin/logout`, {}, {
        withCredentials: true,
        timeout: 15000
      });
      sessionStorage.removeItem(SESSION_KEY);
      setUser(null);
      navigate(LOGIN_PATH, { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Unable to log out. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  };

  const isActive = (to) =>
    location.pathname.toLowerCase().startsWith(to.toLowerCase());

  const menuClass = (to) =>
    `block rounded-md px-3 py-2 text-sm transition hover:bg-slate-800/60 ${
      isActive(to) ? "bg-slate-800 text-white" : "text-slate-300"
    }`;

  const navigation = (
    <>
      <Link
        to="/supreme-admin/dashboard"
        className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition hover:bg-slate-800/70 ${
          isActive("/supreme-admin/dashboard") ? "bg-slate-800 text-white" : "text-slate-100"
        }`}
      >
        <LayoutDashboard className="h-5 w-5" />
        Dashboard
      </Link>

      <DropdownMenu
        title="Tenants"
        icon={Building2}
        index={0}
        openIndex={openDropdown}
        setOpenIndex={setOpenDropdown}
      >
        <li>
          <Link to="/supremeadmin/tenant" className={menuClass("/supremeadmin/tenant")}>
            All Tenants
          </Link>
        </li>
      </DropdownMenu>

      <DropdownMenu
        title="Licensing"
        icon={BadgeCheck}
        index={1}
        openIndex={openDropdown}
        setOpenIndex={setOpenDropdown}
      >
        <li>
          <Link to="/supremeadmin/admin_Menu" className={menuClass("/supremeadmin/admin_Menu")}>
            Licenses
          </Link>
        </li>
      </DropdownMenu>

      <DropdownMenu
        title="Supreme Admin"
        icon={Shield}
        index={2}
        openIndex={openDropdown}
        setOpenIndex={setOpenDropdown}
      >
        <li>
          <Link
            to="/supremeadmin/supremeAdminUsers_List"
            className={menuClass("/supremeadmin/supremeAdminUsers_List")}
          >
            Register
          </Link>
        </li>
      </DropdownMenu>
    </>
  );

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 bg-slate-900/70 p-5">
        <img src={Logo} alt="Infra V5" className="h-12 w-12 rounded" />
        <div className="leading-tight">
          <p className="font-semibold text-slate-100">Infra V5</p>
          <p className="text-sm text-slate-400">Supreme Admin Panel</p>
        </div>
      </div>

      <div className="border-b border-slate-800 px-5 py-4">
        <p className="text-sm text-slate-400">Signed in as</p>
        <p className="break-words text-slate-100">
          <b>{user?.username || "Supreme Admin"}</b>
        </p>
        <p className="mt-1 text-xs text-emerald-400">
          {user?.role || "SUPREME_ADMIN"}
        </p>
      </div>

      <nav className="flex-1 space-y-2 overflow-y-auto px-3 py-4">
        {navigation}
      </nav>

      <div className="border-t border-slate-900 bg-slate-900/70 p-4">
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white transition hover:bg-rose-700 disabled:opacity-60"
        >
          <LogOut className="h-4 w-4" />
          {loggingOut ? "Logging out..." : "Logout"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="sticky top-0 z-40 flex items-center justify-between bg-slate-950 px-4 py-3 text-slate-100 shadow md:hidden">
        <div className="flex items-center gap-3">
          <img src={Logo} alt="Infra V5" className="h-8 w-8 rounded" />
          <span className="font-semibold">Supreme Admin</span>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(!drawerOpen)}
          className="rounded-lg p-2 hover:bg-slate-900"
          aria-label="Toggle menu"
        >
          {drawerOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 transform border-r border-slate-900 bg-slate-950 text-slate-200 transition-transform duration-200 md:hidden ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>

      <aside className="hidden w-72 shrink-0 border-r border-slate-900 bg-slate-950 text-slate-200 md:block">
        <div className="sticky top-0 h-screen">
          {sidebarContent}
        </div>
      </aside>
    </>
  );
}
